import { environment } from '®environments/environment';

import { AppConfig, InvalidConfigException } from './app-config';
import { AppConfigFromWindowContext } from './app-config-from-window-context';
import { AppConfigLocalDev } from './app-config-local-dev';

interface WindowWithShipshapeConfig {
  shipshape: {
    config: AppConfig;
  };
}

export class AppConfigurator {
  /**
   * Get the AppConfig being used by the application, for injection.
   *
   * NOTE: this attempts to load the config from the `window` global; if this is missing,
   * a hard-coded value is used; if it is present but invalid, an exception is thrown.
   *
   * @returns {AppConfig} - The AppConfig to use across the app.
   **/
  getAppConfig(
    isInProduction = environment.production,
    windowWithConfig: unknown = window,
  ): AppConfig {
    // when not in production, use local dev config
    if (!isInProduction) {
      return new AppConfigLocalDev();
    }

    if (!this.checkWindowHasConfig(windowWithConfig)) {
      throw new InvalidConfigException(windowWithConfig, 'windowWithConfig');
    }

    const config = windowWithConfig.shipshape.config;

    // when in production, validate window configuration
    if (!this.isValidConfig(config)) {
      throw new InvalidConfigException(config, 'window.shipshape.config');
    }

    // convert to an AppConfig subtype, and revalidate to ensure properties are transferred
    const appConfig = new AppConfigFromWindowContext(config);
    if (!this.isValidConfig(appConfig)) {
      throw new InvalidConfigException(appConfig, 'AppConfigFromWindowContext');
    }

    return appConfig;
  }

  private checkWindowHasConfig(
    window: unknown,
  ): window is WindowWithShipshapeConfig {
    return typeof window === 'object' &&
      window['shipshape'] &&
      typeof window['shipshape'] === 'object' &&
      window['shipshape']['config'] &&
      typeof window['shipshape']['config'] === 'object'
      ? true
      : false;
  }

  private isValidConfig(config: unknown): config is AppConfig {
    return typeof config === 'object' &&
      config['apiEndpoint'] &&
      config['personaEndpoint'] &&
      config['personaOrigin'] &&
      config['oauthAppId'] &&
      config['talisLoginProvider'] &&
      config['tenant'] &&
      typeof config['tenant'] === 'object' &&
      config['tenant']['conceptBaseUri'] &&
      config['tenant']['hierarchy'] &&
      config['tenant']['httpsBaseUri'] &&
      config['tenant']['name'] &&
      config['tenant']['roleMapping'] &&
      config['tenant']['roleCodesByRoleId'] &&
      config['tenant']['shortCode']
      ? true
      : false;
  }
}

export const appConfigurator: AppConfigurator = new AppConfigurator();

export function configureApp(): AppConfig {
  return appConfigurator.getAppConfig();
}
